<template>
  <v-container class="py-6">
    <v-card class="elevation-12 rounded-xl overflow-hidden glass-card">
      <v-toolbar flat color="transparent" class="px-4 pt-4">
        <div class="d-flex align-center">
          <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
            <v-icon>mdi-account-group</v-icon>
          </v-btn>
          <div>
            <h1 class="text-h5 font-weight-bold primary--text mb-0">
              {{ $t('extracurricular_activity_students') }}
            </h1>
            <span class="text-caption grey--text font-weight-medium">
              Gestión de inscripciones de estudiantes en actividades extracurriculares
            </span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="rounded-pill px-6 elevation-4 shadow-primary hover-scale transition-all"
          @click="openDialog()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          {{ $t('agregar') }}
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-6">
        <v-row class="mb-6 align-center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('Buscar')"
              single-line
              hide-details
              outlined
              dense
              class="rounded-lg bg-grey-lighten-4"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="filterActivityId"
              :items="activities"
              item-text="name"
              item-value="idExtracurricularActivity"
              :label="$t('placeholder_choose_activity')"
              outlined
              dense
              hide-details
              clearable
              class="rounded-lg bg-grey-lighten-4"
              prepend-inner-icon="mdi-filter-variant"
              @change="getEnrollments"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="mappedEnrollments"
          :loading="loadingData"
          :search="search"
          :items-per-page="10"
          class="elevation-0 custom-table"
          :no-data-text="$t('no_resultados')"
          :footer-props="{
            'items-per-page-text': $t('filas_por_pagina'),
            'items-per-page-options': [5, 10, 20, 50]
          }"
        >
          <!-- Student Column -->
          <template v-slot:item.student_name="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar size="40" class="mr-3 elevation-2 border-2-white shadow-sm">
                <v-img :src="item.student_avatar || defaultAvatar"></v-img>
              </v-avatar>
              <div>
                <span class="font-weight-bold text-subtitle-2 d-block">
                  {{ item.student_name }}
                </span>
                <span class="text-caption grey--text">
                  {{ item.classroom_name || 'Sin aula' }}
                </span>
              </div>
            </div>
          </template>

          <!-- Activity Column -->
          <template v-slot:item.activity_name="{ item }">
            <div class="d-flex align-center">
              <v-chip
                outlined
                small
                read-only
                :color="getActivityColor(item.idExtracurricularActivity)"
                class="font-weight-bold"
              >
                <v-icon left x-small>
                  {{ getActivityIcon(item.idExtracurricularActivity) }}
                </v-icon>
                {{ item.activity_name }}
              </v-chip>
            </div>
          </template>

          <!-- Registration Date -->
          <template v-slot:item.registrationDate="{ item }">
            <div class="d-flex flex-column">
              <span class="font-weight-medium grey--text text--darken-2">
                {{ formatDate(item.registrationDate) }}
              </span>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="mr-2 btn-action"
                    v-bind="attrs"
                    v-on="on"
                    @click="editEnrollment(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('modificar') }}</span>
              </v-tooltip>

              <v-tooltip bottom color="error">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    class="btn-action"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmDelete(item)"
                  >
                    <v-icon small>mdi-account-minus-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('delete') }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog Add/Edit -->
    <v-dialog v-model="dialog" max-width="550px" persistent transition="dialog-bottom-transition">
      <v-card class="rounded-xl overflow-hidden glass-card shadow-2xl">
        <v-toolbar flat color="primary" dark>
          <v-toolbar-title class="font-weight-bold">
            {{ editedIndex === -1 ? $t('assign_student_activity') : $t('modificar') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" class="pb-1">
                <span class="text-subtitle-2 font-weight-bold black--text mb-2 d-inline-block">
                  {{ $t('estudiante') }}
                </span>
                <v-autocomplete
                  v-model="editedItem.idStudent"
                  :items="students"
                  :loading="loadingStudents"
                  :item-text="studentText"
                  item-value="id"
                  :label="$t('placeholder_choose_student')"
                  outlined
                  dense
                  class="rounded-lg custom-input"
                  prepend-inner-icon="mdi-account"
                  :rules="[
                    v => !!v || $t('required'),
                    v => !enrollments.some(e => e.idStudent === v && e.idExtracurricularActivity === editedItem.idExtracurricularActivity && e.idStudentExtracurricularActivity !== editedItem.idStudentExtracurricularActivity) || $t('msg_duplicate_student_assignment')
                  ]"
                  :no-data-text="$t('no_resultados')"
                  color="primary"
                >
                  <template v-slot:item="data">
                    <v-list-item-avatar>
                      <img :src="data.item.avatar || defaultAvatar">
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.first_name }} {{ data.item.last_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ getStudentClassroom(data.item.idClassroom) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" class="pt-2">
                <span class="text-subtitle-2 font-weight-bold black--text mb-2 d-inline-block">
                  {{ $t('extracurricular_activities') }}
                </span>
                <v-autocomplete
                  v-model="editedItem.idExtracurricularActivity"
                  :items="activities"
                  :loading="loadingActivities"
                  item-text="name"
                  item-value="idExtracurricularActivity"
                  :label="$t('placeholder_choose_extracurricular_activity')"
                  outlined
                  dense
                  class="rounded-lg custom-input"
                  prepend-inner-icon="mdi-star-circle"
                  :rules="[
                    v => !!v || $t('required'),
                    v => !enrollments.some(e => e.idExtracurricularActivity === v && e.idStudent === editedItem.idStudent && e.idStudentExtracurricularActivity !== editedItem.idStudentExtracurricularActivity) || $t('msg_duplicate_student_assignment')
                  ]"
                  :no-data-text="$t('no_resultados')"
                  color="primary"
                >
                  <template v-slot:item="data">
                    <v-list-item-icon>
                      <v-icon :color="getActivityColor(data.item.idExtracurricularActivity)">
                        {{ getActivityIcon(data.item.idExtracurricularActivity) }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ getBaseActivityName(data.item.idActivity) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="mx-6 opacity-30"></v-divider>

        <v-card-actions class="pa-6 pt-4">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6 grey--text text--darken-2" @click="close">
            {{ $t('cerrar') }}
          </v-btn>
          <v-btn
            color="primary"
            class="rounded-pill px-8 elevation-4 shadow-primary hover-scale transition-all"
            @click="save"
            :loading="saving"
            :disabled="!valid"
          >
            {{ $t('guardar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px" transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden glass-card p-4">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="error" large class="mr-3">mdi-account-remove-outline</v-icon>
          <span class="font-weight-bold">{{ $t('aviso') }}</span>
        </v-card-title>

        <v-card-text class="pt-4 text-body-1 dark--text font-weight-medium">
          {{ $t('msg_delete_activity_student') }}
          <div v-if="itemToDelete" class="mt-4 pa-4 bg-grey-lighten-4 rounded-lg border-l-4-error shadow-inner">
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-account-child</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Estudiante</span>
            </div>
            <div class="font-weight-bold primary--text mb-3">
              {{ getStudentName(itemToDelete.idStudent) }}
            </div>
            
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-star-circle</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Actividad</span>
            </div>
            <div class="font-weight-bold secondary--text">
              {{ getActivityName(itemToDelete.idExtracurricularActivity) }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6" @click="dialogDelete = false">
            No
          </v-btn>
          <v-btn
            color="error"
            class="rounded-pill px-8 elevation-4 shadow-error hover-scale transition-all"
            @click="deleteItemConfirm"
            :loading="deleting"
          >
            {{ $t('Si') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'
import { mapGetters } from 'vuex'

export default {
  name: 'ExtracurricularActivityStudents',
  data: () => ({
    search: '',
    valid: true,
    dialog: false,
    dialogDelete: false,
    loadingData: false,
    loadingStudents: false,
    loadingActivities: false,
    loadingClassrooms: false,
    saving: false,
    deleting: false,
    filterActivityId: null,

    students: [],
    activities: [],
    classrooms: [],
    baseActivities: [],
    enrollments: [],
    
    editedIndex: -1,
    editedItem: {
      idStudentExtracurricularActivity: null,
      idStudent: null,
      idExtracurricularActivity: null
    },
    defaultItem: {
      idStudentExtracurricularActivity: null,
      idStudent: null,
      idExtracurricularActivity: null
    },
    itemToDelete: null,
    
    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    ...mapGetters(['getProfile']),
    headers() {
      return [
        { text: this.$t('estudiante'), value: 'student_name', align: 'start' },
        { text: this.$t('extracurricular_activities'), value: 'activity_name', align: 'start' },
        { text: this.$t('registration_date'), value: 'registrationDate', align: 'start' },
        { text: this.$t('acciones'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    mappedEnrollments() {
      return this.enrollments.map(item => ({
        ...item,
        student_name: this.getStudentName(item.idStudent),
        student_avatar: this.getStudentAvatar(item.idStudent),
        classroom_name: this.getStudentClassroomName(item.idStudent),
        activity_name: this.getActivityName(item.idExtracurricularActivity)
      }))
    }
  },

  created() {
    this.init()
  },

  methods: {
    async init() {
      this.loadingData = true
      try {
        await Promise.all([
          this.getStudents(),
          this.getActivities(),
          this.getBaseActivities(),
          this.getClassrooms(),
          this.getEnrollments()
        ])
      } catch (error) {
        console.error('Error loading initial data:', error)
      } finally {
        this.loadingData = false
      }
    },

    getStudents() {
      this.loadingStudents = true
      return HTTP.get('student/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.students = res.data
      }).finally(() => { this.loadingStudents = false })
    },

    getActivities() {
      this.loadingActivities = true
      return HTTP.get('extracurricular_activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.activities = res.data
      }).finally(() => { this.loadingActivities = false })
    },

    getBaseActivities() {
      return HTTP.get('activities_by_school/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.baseActivities = res.data
      })
    },

    getClassrooms() {
      this.loadingClassrooms = true
      return HTTP.get('classroom/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.classrooms = res.data
      }).finally(() => { this.loadingClassrooms = false })
    },

    getEnrollments() {
      this.loadingData = true
      let url = 'student_extracurricular_activity/'
      if (this.filterActivityId) {
        url = `student_extracurricular_activity_by_activity/${this.filterActivityId}/`
      }

      return HTTP.get(url, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.enrollments = res.data
      }).catch(err => {
        console.error('Error fetching enrollments:', err)
      }).finally(() => { this.loadingData = false })
    },

    studentText(item) {
      return `${item.first_name} ${item.last_name}`
    },

    getStudentName(idStudent) {
      const student = this.students.find(s => s.id === idStudent)
      if (!student) return 'Cargando...'
      return `${student.first_name} ${student.last_name}`
    },

    getStudentAvatar(idStudent) {
      const student = this.students.find(s => s.id === idStudent)
      return student ? student.avatar : null
    },

    getStudentClassroom(idClassroom) {
      const classroom = this.classrooms.find(c => c.id === idClassroom)
      if (!classroom) return 'N/A'
      return `${classroom.grade_name || ''} ${classroom.name || ''}`.trim()
    },

    getStudentClassroomName(idStudent) {
      const student = this.students.find(s => s.id === idStudent)
      if (!student || !student.idClassroom) return 'Sin aula'
      const classroom = this.classrooms.find(c => (c.id === student.idClassroom || c.idClassroom === student.idClassroom))
      if (!classroom) return 'Sin aula'
      return `${classroom.grade_name || ''} ${classroom.name || ''}`.trim()
    },

    getActivityName(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      return activity ? activity.name : 'Cargando...'
    },

    getBaseActivityName(idBaseActivity) {
      const base = this.baseActivities.find(b => b.idActivity === idBaseActivity)
      return base ? base.name : ''
    },

    getActivityIcon(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      if (!activity) return 'mdi-star'
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity)
      if (!base) return 'mdi-star'
      return base.workshop ? 'mdi-palette' : 'mdi-run-fast'
    },

    getActivityColor(idActivity) {
      const activity = this.activities.find(a => a.idExtracurricularActivity === idActivity)
      if (!activity) return 'grey'
      const base = this.baseActivities.find(b => b.idActivity === activity.idActivity)
      if (!base) return 'grey'
      return base.workshop ? 'success' : 'info'
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },

    openDialog() {
      this.editedIndex = -1
      this.editedItem = Object.assign({}, this.defaultItem)
      // If we have a filter active, pre-select it
      if (this.filterActivityId) {
        this.editedItem.idExtracurricularActivity = this.filterActivityId
      }
      this.dialog = true
      this.$nextTick(() => { this.$refs.form.resetValidation() })
    },

    editEnrollment(item) {
      this.editedIndex = this.enrollments.findIndex(e => e.idStudentExtracurricularActivity === item.idStudentExtracurricularActivity)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    confirmDelete(item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    save() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      const method = this.editedIndex > -1 ? 'patch' : 'post'
      const baseUrl = 'student_extracurricular_activity/'
      const id = this.editedItem.idStudentExtracurricularActivity
      const url = this.editedIndex > -1 ? `${baseUrl}${id}/` : baseUrl

      HTTP({
        method, url, data: this.editedItem,
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.editedIndex > -1 ? this.$t('msg_student_activity_updated') : this.$t('msg_student_activity_added'), 'success')
        this.getEnrollments()
        this.close()
      }).catch(err => {
        console.error('Error saving enrollment:', err)
        this.showSnackbar('Error al guardar la inscripción', 'error')
      }).finally(() => { this.saving = false })
    },

    deleteItemConfirm() {
      this.deleting = true
      const id = this.itemToDelete.idStudentExtracurricularActivity
      
      HTTP.delete(`student_extracurricular_activity/${id}/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar(this.$t('msg_student_activity_deleted'), 'success')
        this.getEnrollments()
        this.dialogDelete = false
      }).catch(err => {
        console.error('Error deleting enrollment:', err)
        this.showSnackbar('Error al dar de baja', 'error')
      }).finally(() => { this.deleting = false })
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07) !important;
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.border-2-white {
  border: 2px solid white;
}

.shadow-primary {
  box-shadow: 0 4px 14px 0 rgba(var(--v-primary-base), 0.3) !important;
}

.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.3) !important;
}

.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}

.hover-scale {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-scale:hover {
  transform: translateY(-2px) scale(1.02);
}

.transition-all {
  transition: all 0.3s ease;
}

.custom-table >>> thead th {
  background-color: #f8f9fa !important;
  color: #5f6368 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.75px;
  font-size: 0.7rem !important;
  height: 64px !important;
  border-bottom: 2px solid #edf2f7 !important;
}

.custom-table >>> tbody tr {
  transition: background-color 0.2s ease;
}

.custom-table >>> tbody tr:hover {
  background-color: rgba(var(--v-primary-base), 0.03) !important;
}

.custom-table >>> .v-data-footer {
  border-top: 1px solid #edf2f7 !important;
}

.btn-action {
  transition: all 0.2s ease;
  background-color: #f8f9fa;
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.custom-input >>> .v-input__slot {
  transition: all 0.3s ease;
}

.custom-input >>> .v-input__slot:hover {
  background: #f1f3f4 !important;
}

.border-l-4-error {
  border-left: 4px solid #ff5252;
}

.tracking-wider {
  letter-spacing: 0.5px;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}
</style>
